import { changeRequestState, type WikiChangeItem, type WikiChangeSet } from "@esi/shared";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileMinus,
  FilePen,
  FilePlus,
  FileText,
  FolderTree,
  MessageSquareWarning,
  MoreHorizontal,
  RotateCcw,
  TriangleAlert,
  X,
} from "lucide-react";
import { type KeyboardEvent, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { itemName } from "./ChangeSetReview.tsx";
import { type ItemDecision, outlineGroupOf, STRUCTURE } from "./ReviewReader.tsx";
import { itemSize } from "./sizes.ts";

export type ReviewTab = "changes" | "discussion";

/** Arrow keys, Home and End over a tablist's tabs (roving tabindex, D14). */
function roving(
  e: KeyboardEvent<HTMLElement>,
  ids: readonly string[],
  current: string,
  box: HTMLElement | null,
  attr: string,
): boolean {
  const at = ids.indexOf(current);
  const next =
    e.key === "ArrowRight"
      ? ids[(at + 1) % ids.length]
      : e.key === "ArrowLeft"
        ? ids[(at - 1 + ids.length) % ids.length]
        : e.key === "Home"
          ? ids[0]
          : e.key === "End"
            ? ids.at(-1)
            : undefined;
  if (next === undefined) {
    return false;
  }
  e.preventDefault();
  box?.querySelector<HTMLElement>(`[${attr}="${CSS.escape(next)}"]`)?.focus();
  return true;
}

const opIcon = (item: WikiChangeItem | undefined) =>
  !item
    ? FolderTree
    : item.op === "create"
      ? FilePlus
      : item.op === "delete"
        ? FileMinus
        : item.op === "edit"
          ? FilePen
          : FileText;

/**
 * The sticky strip (D14, D15, #340 rework): one compact line. The page switcher (the current
 * change's name, a menu of every change with its size and risk, and the current page's Approve,
 * Reject or Reopen when the set has more than one item), the page's size and risk, the
 * "‹ N open ›" stepper, Changes | Discussion as a light segmented control, and a view menu with
 * Show issues inline (the dock is the pane's own switch). On a phone the line wraps once.
 */
export function ReviewStrip(props: {
  set: WikiChangeSet;
  tab: ReviewTab;
  onTab: (tab: ReviewTab) => void;
  /** Open set-level threads, shown on Discussion. */
  discussionCount: number;
  selected: string | null;
  onSelect: (id: string) => void;
  /** Possible contradictions per item. */
  flags: ReadonlyMap<string, number>;
  decisions: (item: WikiChangeItem) => ItemDecision[];
  onDecide: (decision: ItemDecision, itemId: string) => void;
  busy: boolean;
  panelId: string;
  /** The selected page's open items; null when the page has no passages (Structure, a deletion). */
  open: number | null;
  onStep: (direction: -1 | 1) => void;
  onOpenFirst: () => void;
  inline: boolean;
  onInline: (on: boolean) => void;
  /** The stepper sits here beside the pane; the bottom sheet holds its own (D15). */
  stepper: boolean;
  /**
   * "What changed since my last review" (S5, D18): offered once the viewer decided on the set;
   * absent otherwise.
   */
  since?: { on: boolean; onChange: (on: boolean) => void };
}) {
  const { t } = useTranslation();
  const decideLabel = useId();
  const { set } = props;
  const requests = changeRequestState(set);
  const tabBox = useRef<HTMLDivElement>(null);
  const content = set.items.filter((i) => outlineGroupOf(i) !== "structure");
  const structure = set.items.filter((i) => outlineGroupOf(i) === "structure");
  const pages = [
    ...content.filter((i) => outlineGroupOf(i) === "content").map((i) => i.id),
    ...(structure.length ? [STRUCTURE] : []),
    ...content.filter((i) => outlineGroupOf(i) === "removals").map((i) => i.id),
  ];
  const active = (pages.includes(props.selected ?? "") ? props.selected : pages[0]) ?? "";
  const tabs: ReviewTab[] = ["changes", "discussion"];
  const describe = (pageId: string) => {
    const item = set.items.find((i) => i.id === pageId);
    const flags = item ? (props.flags.get(item.id) ?? 0) : 0;
    const requested = item ? requests.onItem(item.id) : false;
    const size = item
      ? item.op === "create"
        ? t("wiki.review.strip.new")
        : item.op === "delete"
          ? t("wiki.review.strip.removed")
          : (itemSize(item) ?? t(`wiki.changes.op.${item.op}`))
      : String(structure.length);
    return {
      item,
      Icon: opIcon(item),
      name: item ? itemName(item) : t("wiki.review.outline.tree"),
      size,
      flags,
      requested,
      facts: [
        ...(item && item.status !== "pending" ? [t(`wiki.changes.itemState.${item.status}`)] : []),
        ...(flags ? [t("wiki.review.outline.flags", { count: flags })] : []),
        ...(requested ? [t("wiki.changes.changesRequested")] : []),
      ],
    };
  };
  const risk = (flags: number, requested: boolean) =>
    flags || requested ? (
      <span className="flex shrink-0 items-center gap-0.5 text-xs text-warning-fg tabular-nums">
        <TriangleAlert aria-hidden="true" className="size-3.5" />
        {flags ? flags : null}
      </span>
    ) : null;
  const current = describe(active);
  const index = pages.indexOf(active);
  const decisions = current.item ? props.decisions(current.item) : [];
  const changes = props.tab === "changes";
  const viewMenu = changes && (props.open !== null || !!props.since);
  // The switcher's name carries what the line shows beside it: the size and the risk.
  const currentName = [current.name, current.size, ...current.facts].join(", ");
  // One page and nothing to decide: a menu would only repeat it, so the line names it.
  const switcher = pages.length > 1 || decisions.length > 0;
  const kindIcon = (
    <current.Icon aria-hidden="true" className="size-4 shrink-0 text-fg-muted" strokeWidth={1.75} />
  );
  return (
    <div
      data-review-strip=""
      data-esi-content="wiki"
      data-selection-ignore=""
      className="sticky top-0 z-10 flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-line bg-background px-4 py-2 sm:px-6"
    >
      {changes && pages.length ? (
        // At least 12rem, so on a phone the line wraps once rather than squeezing the name away.
        <div className="flex min-w-48 flex-1 items-center gap-2">
          {switcher ? null : (
            <p
              data-page-switcher=""
              className={cn(
                "flex h-8 min-w-0 items-center gap-1.5 px-2 text-sm font-medium text-fg",
                current.item?.status === "rejected" && "text-fg-secondary line-through",
              )}
            >
              {kindIcon}
              <span className="min-w-0 truncate">{current.name}</span>
            </p>
          )}
          {switcher ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-page-switcher=""
                  aria-label={t("wiki.review.strip.page", {
                    title: currentName,
                    index: index + 1,
                    count: pages.length,
                  })}
                  className={cn(
                    "flex h-8 max-w-80 min-w-0 items-center gap-1.5 rounded-md px-2 text-sm font-medium text-fg transition-colors duration-(--duration-fast) hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none data-[state=open]:bg-surface-hover",
                    current.item?.status === "rejected" && "text-fg-secondary line-through",
                  )}
                >
                  {kindIcon}
                  <span className="min-w-0 truncate">{current.name}</span>
                  <ChevronDown aria-hidden="true" className="size-3.5 shrink-0 text-fg-muted" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-80 max-w-[calc(100vw-2rem)]">
                <DropdownMenuLabel className="text-xs font-normal text-fg-muted">
                  {t("wiki.review.strip.position", { index: index + 1, count: pages.length })}
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup value={active} onValueChange={props.onSelect}>
                  {pages.map((pageId) => {
                    const page = describe(pageId);
                    return (
                      <DropdownMenuRadioItem
                        key={pageId}
                        value={pageId}
                        data-page={pageId}
                        className={cn(
                          page.item?.status === "rejected" && "text-fg-secondary line-through",
                        )}
                      >
                        <page.Icon
                          aria-hidden="true"
                          className="size-4 text-fg-muted"
                          strokeWidth={1.75}
                        />
                        <span className="min-w-0 flex-1 truncate">{page.name}</span>
                        <span className="shrink-0 text-xs text-fg-muted tabular-nums">
                          {page.size}
                        </span>
                        {risk(page.flags, page.requested)}
                        {page.facts.length ? (
                          <span className="sr-only">, {page.facts.join(", ")}</span>
                        ) : null}
                      </DropdownMenuRadioItem>
                    );
                  })}
                </DropdownMenuRadioGroup>
                {decisions.length && current.item ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup aria-labelledby={decideLabel}>
                      <DropdownMenuLabel
                        id={decideLabel}
                        className="truncate text-xs font-normal text-fg-muted"
                      >
                        {t("wiki.review.outline.menu", { title: current.name })}
                      </DropdownMenuLabel>
                      {decisions.map((decision) => (
                        <DropdownMenuItem
                          key={decision}
                          disabled={props.busy}
                          onSelect={() => current.item && props.onDecide(decision, current.item.id)}
                        >
                          {decision === "approve" ? (
                            <Check aria-hidden="true" />
                          ) : decision === "reject" ? (
                            <X aria-hidden="true" />
                          ) : (
                            <RotateCcw aria-hidden="true" />
                          )}
                          {t(`wiki.changes.${decision}`)}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuGroup>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          <span className="shrink-0 text-xs text-fg-muted tabular-nums">{current.size}</span>
          {risk(current.flags, current.requested)}
          {!switcher && current.facts.length ? (
            <span className="sr-only">{current.facts.join(", ")}</span>
          ) : null}
        </div>
      ) : (
        <span className="flex-1" />
      )}
      {changes && props.open !== null && props.stepper ? (
        // biome-ignore lint/a11y/useSemanticElements: a toolbar-like group, not a form's fieldset.
        <div
          role="group"
          aria-label={t("wiki.review.stepper.label")}
          className="flex shrink-0 items-center"
        >
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("wiki.review.stepper.previous")}
            disabled={!props.open}
            onClick={() => props.onStep(-1)}
          >
            <ChevronLeft aria-hidden="true" />
          </Button>
          <button
            type="button"
            data-open-count=""
            disabled={!props.open}
            onClick={props.onOpenFirst}
            className={cn(
              "flex h-8 items-center gap-1.5 rounded-md px-1.5 text-[13px] font-medium whitespace-nowrap tabular-nums focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
              props.open ? "text-warning-fg hover:bg-surface-hover" : "text-fg-muted",
            )}
          >
            <MessageSquareWarning aria-hidden="true" className="size-3.5" />
            {t("wiki.review.stepper.open", { count: props.open })}
          </button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("wiki.review.stepper.next")}
            disabled={!props.open}
            onClick={() => props.onStep(1)}
          >
            <ChevronRight aria-hidden="true" />
          </Button>
        </div>
      ) : null}
      <div className="flex shrink-0 items-center gap-1">
        <div
          ref={tabBox}
          role="tablist"
          aria-label={t("wiki.review.strip.views")}
          className="inline-flex gap-0.5 rounded-md p-0.5"
        >
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              data-review-tab={tab}
              aria-selected={props.tab === tab}
              aria-controls={props.panelId}
              tabIndex={props.tab === tab ? 0 : -1}
              onClick={() => props.onTab(tab)}
              onKeyDown={(e) => {
                roving(e, tabs, tab, tabBox.current, "data-review-tab");
              }}
              className={cn(
                "flex h-7 items-center gap-1.5 rounded-[5px] px-2.5 text-[13px] whitespace-nowrap text-fg-secondary transition-colors duration-(--duration-fast) hover:text-fg focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                props.tab === tab && "bg-surface-sunken font-medium text-fg",
              )}
            >
              {t(`wiki.review.strip.${tab}`)}
              <span className="text-xs font-normal text-fg-muted tabular-nums">
                {tab === "changes" ? set.items.length : props.discussionCount}
              </span>
            </button>
          ))}
        </div>
        {viewMenu ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label={t("wiki.review.strip.viewMenu")}>
                <MoreHorizontal aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-52">
              {props.open !== null ? (
                <DropdownMenuCheckboxItem
                  checked={props.inline}
                  onCheckedChange={(on) => props.onInline(on === true)}
                >
                  {t("wiki.review.strip.inline")}
                </DropdownMenuCheckboxItem>
              ) : null}
              {props.since ? (
                <DropdownMenuCheckboxItem
                  checked={props.since.on}
                  onCheckedChange={(on) => props.since?.onChange(on === true)}
                >
                  {t("wiki.history.review.toggle")}
                </DropdownMenuCheckboxItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </div>
  );
}
